import ActionTextIcon from "@/components/icons/ActionTextIcon";
import Ellipsis from "@/components/views/Ellipsis";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { formatLocaleCurrency } from "@/utils/formatUtils";
import { URLQuery } from "@/utils/queryUtils";
import { Col, Row, Table } from "antd";
import { ColumnsType } from "antd/es/table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useIncludeSpecialCommission } from "../../../../../common/api/queries";
import { rowGutter, TableSizes } from "../../../../../common/constants";
import { Permission } from "../../../../../common/security/enums";
import { formatAgentName } from "../../../../agent/utils";
import { useHasPermissions } from "../../../../auth/hooks";
import { SpecialCommissionsReport } from "../../../special/enums";
import { SpecialCommission, SpecialCommissionsFilterPageResult } from "../../../special/types";
import { CommissionsBatchStep } from "../../enums";
import { SpecialCommissionExcludeForm } from "../forms/SpecialCommissionExcludeForm";

interface Props {
  commissionsPage: SpecialCommissionsFilterPageResult<SpecialCommission>;
  loading?: boolean;
}

export const BatchSpecialCommissionTable = ({ commissionsPage, loading }: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const hasManageCommissionsPermission = useHasPermissions(Permission.COMMISSIONS_MANAGE);

  const [commissionToExclude, setCommissionToExclude] = useState<SpecialCommission>();

  const includeSpecialCommission = useIncludeSpecialCommission();

  const columns = useMemo(() => {
    const { batch, agent, report } = commissionsPage;

    const items: ColumnsType<SpecialCommission> = [
      {
        key: "code",
        title: t("commissions.special.attrs.code"),
        width: 100,
        ellipsis: { showTitle: false },
        render: (_, record) => <Ellipsis>{record.code}</Ellipsis>,
      },
      {
        key: "name",
        title: t("commissions.special.attrs.name"),
        width: 252,
        ellipsis: { showTitle: false },
        render: (_, record) => <Ellipsis>{record.name}</Ellipsis>,
      },
      {
        key: "payer",
        title: t("commissions.special.attrs.payer"),
        width: 155,
        ellipsis: { showTitle: false },
        render: (_, record) => <Ellipsis>{formatAgentName(record.payer)}</Ellipsis>,
      },
      {
        key: "payee",
        title: t("commissions.special.attrs.payee"),
        width: 155,
        ellipsis: { showTitle: false },
        render: (_, record) => <Ellipsis>{formatAgentName(record.payee)}</Ellipsis>,
      },
      {
        key: "amount",
        title: t("commissions.special.attrs.commissionAmount"),
        align: "right",
        width: 100,
        render: (_, record) =>
          formatLocaleCurrency(record.payee.id === agent.id ? record.commissionAmount : -record.commissionAmount),
      },
    ];

    if (batch.step === CommissionsBatchStep.RECAPITULATION && hasManageCommissionsPermission) {
      items.push({
        key: "actions",
        align: "right",
        fixed: "right",
        width: 90,
        render: (_, record) =>
          report === SpecialCommissionsReport.INCLUDED ? (
            <ActionTextIcon
              icon="branch"
              color="orange"
              text={t("common.exclude")}
              disabled={batch.stepChangeInProgress}
              onClick={() => setCommissionToExclude(record)}
            />
          ) : (
            <ActionTextIcon
              icon="pull-request"
              color="green"
              text={t("common.include")}
              disabled={batch.stepChangeInProgress}
              onClick={() =>
                includeSpecialCommission.mutate({
                  id1: batch.id,
                  id2: agent.id,
                  id3: record.id,
                  object: { optimisticLockVersion: record.optimisticLockVersion },
                })
              }
            />
          ),
      });
    }

    return items;
  }, [commissionsPage, hasManageCommissionsPermission]);

  return (
    <>
      <div className={commissionsPage.totalElementsCount > 0 ? "table-header-margin" : "margin-bottom-small"}>
        <b>{t("commissions.special.helpers.filteredCommissionAmountsSum")}: </b>
        {formatLocaleCurrency(commissionsPage.commissionAmountsSum)}
      </div>

      <Table<SpecialCommission>
        {...tableStandardProps()}
        columns={columns}
        scroll={{ x: TableSizes.MEDIUM }}
        dataSource={commissionsPage.pageData}
        loading={loading}
        expandable={{
          rowExpandable: () => commissionsPage.report === SpecialCommissionsReport.EXCLUDED,
          expandedRowRender: (record) => (
            <Row gutter={rowGutter} justify="center" className="margin-top-small margin-bottom-small">
              <Col flex="140px">
                <b>{t("commissions.special.attrs.exclusionReason")}:</b>
              </Col>
              <Col flex="1 1 0">{record.exclusionReason}</Col>
            </Row>
          ),
        }}
        pagination={{
          ...paginationTableProps,
          current: commissionsPage.pageIndex + 1,
          pageSize: commissionsPage.pageSize,
          total: commissionsPage.totalElementsCount,
          onChange: (pageIndex) => navigate({ search: URLQuery.getTableChangePageIndex(pageIndex) }, { replace: true }),
        }}
      />

      <SpecialCommissionExcludeForm
        batchId={commissionsPage.batch.id}
        agentId={commissionsPage.agent.id}
        commission={commissionToExclude}
        onFormCancel={() => setCommissionToExclude(undefined)}
      />
    </>
  );
};
